import { validateCitySlug } from '@utils';
import { stateAbbreviationNameSlugMap } from '../../../../../../helpers';

import { canadianProvinces } from '../../../../../../constants/canadian_provinces';

import Vendor from '../../../../../decorators/vendor';
import VendorAnalytics from '../../../../../decorators/vendorAnalytics';
import { marketplaceHomeRoute } from '../../../../../routes/routes';
import { codeToSlugNameMap } from '../../../../../utils/categoryNameMappings';
import Constants from '../../constants';

const BASE_URL = marketplaceHomeRoute.path;

const vendorAnalyticsProps = (vendor) => {
	if (vendor) {
		const decoratedVendor = new Vendor(vendor);
		const vendorAnalytics = new VendorAnalytics(decoratedVendor);
		return vendorAnalytics.buildEventProps();
	}

	return {};
};

const addBreadcrumb = (breadcrumbs, props) => {
	const {
		url: urlFragment,
		label,
		categoryCode,
		vendorAnalytics = {},
		sourcePage,
	} = props;

	const fullUrl = `${BASE_URL}${urlFragment || ''}`;

	breadcrumbs.push({
		url: fullUrl,
		label,
		analytics: {
			vendorCategoryCode: categoryCode,
			sourceContent: 'breadcrumb',
			...vendorAnalytics,
			sourcePage,
			selection: label,
		},
	});
};

const categoryInfo = (categoryCode) => {
	const category = codeToSlugNameMap[categoryCode];

	// Because of the SEO value, we want to use
	// Wedding Venues instead of Reception Venues
	if (category && categoryCode === 'REC') {
		return { ...category, name: 'Wedding Venues' };
	}

	return category || {};
};

export const breadcrumbsLevels = (categoryCode, stateCode, city, vendor) => {
	const { SOURCE_PAGE } = Constants;
	const category = categoryInfo(categoryCode);
	const { stateName, stateSlug } =
		stateAbbreviationNameSlugMap[stateCode] || {};
	const breadcrumbs = [];

	// Pre-compute once — avoids recreating Vendor/VendorAnalytics instances per breadcrumb
	const vendorAnalytics = vendorAnalyticsProps(vendor);

	// Determine the current page (source page) based on the deepest level of context provided
	// This represents the page the user is currently on when clicking a breadcrumb
	let sourcePage;
	if (category && stateCode && city && vendor) {
		sourcePage =
			vendor.purchaseStatus === 'PAID'
				? SOURCE_PAGE.STOREFRONT_PAID
				: SOURCE_PAGE.STOREFRONT_UNPAID;
	} else if (category && stateCode && city) {
		sourcePage = SOURCE_PAGE.CATEGORY_CITY;
	} else if (category && stateCode) {
		sourcePage = SOURCE_PAGE.CATEGORY_STATE;
	} else if (category) {
		sourcePage = SOURCE_PAGE.CATEGORY;
	} else {
		sourcePage = SOURCE_PAGE.HOME;
	}

	// Build breadcrumbs root-to-deepest using push (O(1)) instead of unshift (O(n))
	addBreadcrumb(breadcrumbs, {
		label: 'Wedding Vendors',
		url: '',
		sourcePage,
		categoryCode,
		vendorAnalytics,
	});

	if (category?.href) {
		addBreadcrumb(breadcrumbs, {
			label: category.name,
			url: `/${category.href}?redirectToCity=false`,
			sourcePage,
			categoryCode,
			vendorAnalytics,
		});
	}

	if (
		category?.href &&
		stateCode &&
		!canadianProvinces.includes(stateCode) &&
		stateCode !== 'DC'
	) {
		// on state pages we are displaying only state name
		// on search pages state name + category name
		addBreadcrumb(breadcrumbs, {
			label: city ? `${stateName} ${category.name}` : stateName,
			url: `/${category.href}/${stateSlug}?redirectToCity=false`,
			sourcePage,
			categoryCode,
			vendorAnalytics,
		});
	}

	if (category?.href && stateCode && city) {
		const slugifiedCity = validateCitySlug(city);
		addBreadcrumb(breadcrumbs, {
			label: `${city} ${category.name}`,
			url: `/${category.href}-${slugifiedCity}-${stateCode}?sort=featured`.toLowerCase(),
			sourcePage,
			categoryCode,
			vendorAnalytics,
		});
	}

	// storefront level is inactive - no link, no need for tracking info
	if (category && stateCode && city && vendor) {
		breadcrumbs.push({
			url: '',
			label: vendor.name,
			analytics: {},
		});
	}

	return breadcrumbs;
};
